/**
 * Auth API Client
 *
 * Typed fetch functions for authentication endpoints.
 * All functions use the shared API client from @shared/lib/api/client.
 *
 * Translates between the camelCase types the React code uses and the
 * snake_case payloads the Django serializers expect / return.
 *
 * @module auth/api/auth-client
 */

import { apiClient } from "@shared/lib/api/client";
import type {
  LoginRequest,
  RegisterRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  VerifyEmailRequest,
  ResendVerificationRequest,
  AuthResponse,
  TokenResponse,
  SuccessResponse,
  User,
} from "./types";

const AUTH_BASE = "/api/v1/auth";

interface DjangoAuthPayload {
  user: User;
  access_token?: string;
  refresh_token?: string;
  is_email_verified?: boolean;
  message?: string;
}

function normalizeAuth(raw: DjangoAuthPayload): AuthResponse {
  return {
    user: raw.user,
    accessToken: raw.access_token ?? "",
    refreshToken: raw.refresh_token ?? "",
    isEmailVerified: raw.is_email_verified,
    message: raw.message,
  };
}

/**
 * Sign in with email and password.
 */
export async function login(credentials: LoginRequest): Promise<AuthResponse> {
  const raw = await apiClient.post<DjangoAuthPayload>(
    `${AUTH_BASE}/login/`,
    {
      email: credentials.email,
      password: credentials.password,
      remember_me: credentials.rememberMe ?? false,
    },
    { auth: false, skipAuthRefresh: true },
  );
  return normalizeAuth(raw);
}

/**
 * Register a new account.
 */
export async function register(data: RegisterRequest): Promise<AuthResponse> {
  const raw = await apiClient.post<DjangoAuthPayload>(
    `${AUTH_BASE}/register/`,
    {
      email: data.email,
      password: data.password,
      confirm_password: data.confirmPassword ?? data.password,
      first_name: data.firstName,
      last_name: data.lastName,
      terms_accepted: data.termsAccepted ?? true,
    },
    { auth: false, skipAuthRefresh: true },
  );
  return normalizeAuth(raw);
}

/**
 * Sign out the current user. Requires the refresh token to blacklist it.
 */
export async function logout(refreshToken: string): Promise<SuccessResponse> {
  return apiClient.post<SuccessResponse>(
    `${AUTH_BASE}/logout/`,
    {
      refresh: refreshToken,
    },
    { skipAuthRefresh: true },
  );
}

/**
 * Request a password reset email.
 */
export async function forgotPassword(
  data: ForgotPasswordRequest
): Promise<SuccessResponse> {
  return apiClient.post<SuccessResponse>(
    `${AUTH_BASE}/forgot-password/`,
    data,
    { auth: false, skipAuthRefresh: true },
  );
}

/**
 * Reset password using the OTP the user received by email.
 */
export async function resetPassword(
  data: ResetPasswordRequest
): Promise<SuccessResponse> {
  return apiClient.post<SuccessResponse>(
    `${AUTH_BASE}/reset-password/`,
    {
      email: data.email,
      otp: data.code,
      password: data.password,
    },
    { auth: false, skipAuthRefresh: true },
  );
}

/**
 * Verify email using the OTP the user received by email.
 */
export async function verifyEmail(
  data: VerifyEmailRequest
): Promise<SuccessResponse> {
  return apiClient.post<SuccessResponse>(
    `${AUTH_BASE}/verify-email/`,
    {
      email: data.email,
      code: data.code,
    },
    { auth: false, skipAuthRefresh: true },
  );
}

/**
 * Resend the email verification OTP.
 *
 * Backend rate limits to 1 request per 2 minutes per user (and 3/hour per IP).
 */
export async function resendVerification(
  data: ResendVerificationRequest
): Promise<SuccessResponse> {
  return apiClient.post<SuccessResponse>(
    `${AUTH_BASE}/resend-verification/`,
    {
      email: data.email,
    },
    { auth: false, skipAuthRefresh: true },
  );
}

/**
 * Refresh the auth token. Backend returns `{ access }` (and optionally `refresh`).
 */
export async function refreshToken(refresh: string): Promise<TokenResponse> {
  const raw = await apiClient.post<{ access: string; refresh?: string }>(
    `${AUTH_BASE}/refresh/`,
    { refresh },
    { auth: false, skipAuthRefresh: true },
  );
  return { accessToken: raw.access, refreshToken: raw.refresh };
}

/**
 * Get current user profile.
 */
export async function getMe(): Promise<User> {
  return apiClient.get<User>(`${AUTH_BASE}/me/`);
}
